/**
 * Client-side straddle pricing: the only place scenario values are computed.
 *
 * The backend returns entry facts and per-leg inputs (`_legs`). Everything else
 * is priced here, so the heatmap, sliders and detail table can reprice on every
 * settings change with no API call, and there is no server copy to drift.
 *
 * A row is one call plus one put, each with its own strike, IV and expiration.
 * spreadCalculations.js can't do this: it assumes one option type and one
 * expiration, and clamps value to a strike width that a straddle doesn't have.
 *
 * The rule for one leg, at horizon h and column move pct:
 *
 *   exit  = min(h, days to expiry)
 *   S     = S0 x (1 + pct x exit / h)     Steady: straight line to the column's price
 *   S     = S0 x (1 + pct)                Early: the move lands before the leg expires
 *   T     = 0 once the leg has expired, otherwise (days - h) / 365
 *
 * T is passed to blackScholes unfloored because T = 0 returns exact intrinsic
 * value. The 0.0001-year floor in the other heatmaps would give an expired leg
 * phantom time value.
 */
import { blackScholes } from './blackScholes';
import { getColorForGainPct } from '../theme/heatmapPalette';

const RISK_FREE_RATE = 0.05;

export const MOVE_STEADY = 'steady';
export const MOVE_EARLY = 'early';

export const VIEW_NET = 'net';
export const VIEW_CALL = 'call';
export const VIEW_PUT = 'put';

export const DISPLAY_RETURN = 'return';
export const DISPLAY_PNL = 'pnl';
export const DISPLAY_MULTIPLIER = 'multiplier';

export const RANK_BOTH_WAYS = 'both-ways';
export const RANK_HIGHEST = 'highest-gain';
export const RANK_AVERAGE = 'average';
export const RANK_BREAKEVEN = 'breakeven';

/** Quick horizons, and the days compared when a row is expanded. */
export const HORIZON_CHIPS = [10, 30, 90];

/** Planned-move options for ranking, as a % move in either direction. */
export const PLANNED_MOVE_CHIPS = [5, 10, 15, 20, 30];

/** IV at exit relative to today's, applied to legs still alive at the horizon. */
export const IV_SHIFT_CHIPS = [-30, -20, -10, 0, 10, 20];

/** Scenario columns offered in the settings panel. */
export const STRADDLE_SCENARIO_OPTIONS = [
  -50, -35, -30, -20, -15, -10, -5, -2, 0, 2, 5, 10, 15, 20, 30, 35, 50, 100,
];

/** A straddle pays both ways, so the default columns are symmetric around 0. */
export const DEFAULT_STRADDLE_SCENARIOS = new Set([-30, -20, -10, -5, 0, 5, 10, 20, 30]);

const STORAGE_KEY = 'straddles_heatmap_user_scenarios';

/** How far the model may miss today's mark before a row loses its rank. */
const MODEL_GAP_LIMIT_PCT = 10;

/** Per-share P&L treated as exactly zero when classifying a breakeven. */
const BREAKEVEN_EPSILON = 1e-4;

export function getUserStraddleScenarios() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_STRADDLE_SCENARIOS);
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return new Set(DEFAULT_STRADDLE_SCENARIOS);
    return new Set(arr.map(Number).filter((n) => !Number.isNaN(n)));
  } catch {
    return new Set(DEFAULT_STRADDLE_SCENARIOS);
  }
}

export function saveUserStraddleScenarios(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
}

export function clearUserStraddleScenarios() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Horizons
// ---------------------------------------------------------------------------

/** Days to a leg's expiry: fractional when known, the clock its IV was solved on. */
export function legDays(leg) {
  const fractional = parseFloat(leg.dte_frac);
  return Number.isFinite(fractional) ? fractional : parseFloat(leg.dte);
}

export function firstExpiryDays(row) {
  return Math.min(parseFloat(row['Call DTE']), parseFloat(row['Put DTE']));
}

export function lastExpiryDays(row) {
  return Math.max(parseFloat(row['Call DTE']), parseFloat(row['Put DTE']));
}

/**
 * One calendar horizon for a row. Percent mode is a share of the time until the
 * FIRST leg expires, so 100% means exiting that day. Both legs always use the
 * same date, because one stock price at one moment is what a cell describes.
 */
export function resolveHorizon(row, daysToExit, daysToExitPercent = null) {
  if (daysToExitPercent !== null && daysToExitPercent !== undefined) {
    return Math.max(1, Math.round(firstExpiryDays(row) * daysToExitPercent));
  }
  if (daysToExit !== null && daysToExit !== undefined) return daysToExit;
  return Math.max(1, Math.round(firstExpiryDays(row) * 0.5));
}

/** Starting horizon for a new run: halfway to the soonest expiry on screen. */
export function seedHorizon(rows) {
  const days = (rows || []).map(firstExpiryDays).filter((d) => d > 0);
  if (days.length === 0) return 30;
  return Math.max(1, Math.round(Math.min(...days) * 0.5));
}

/** Slider ceiling: the longest leg, so day 30 stays reachable beside a 14-day put. */
export function maxHorizonDays(rows) {
  const days = (rows || []).map(lastExpiryDays).filter((d) => d > 0);
  return days.length ? Math.max(...days) : 365;
}

/** True once the horizon is past both expiries: the position no longer exists. */
export function isFullySettled(row, horizon) {
  const { call, put } = row._legs || {};
  return !!call && !!put && horizon >= legDays(call) && horizon >= legDays(put);
}

// ---------------------------------------------------------------------------
// Position sizing
// ---------------------------------------------------------------------------

/** Sizing applies only with money on both sides: a $0 side isn't a straddle. */
export function isSizingActive(sizing) {
  return !!sizing && sizing.enabled === true
    && Number(sizing.callBudget) > 0 && Number(sizing.putBudget) > 0;
}

/** Cost of one contract in cents: premium x 100 shares x 100 cents. */
const contractCents = (premium) => Math.round(parseFloat(premium) * 10000);

/**
 * Whole contracts for a side's budget: the nearest count, half rounding up, and
 * never fewer than one.
 *
 * Integer cents, because a tie must not round the wrong way on float noise.
 * Returns 0 only for a leg with no usable premium.
 */
export function contractsForBudget(budget, premium) {
  const budgetCents = Math.round(Number(budget) * 100);
  const costCents = contractCents(premium);
  if (!(costCents > 0)) return 0;
  return Math.max(1, Math.floor((2 * budgetCents + costCents) / (2 * costCents)));
}

/** Joins a straddle's key to its split, so each split is its own row to star or drop. */
const SPLIT_KEY_SEPARATOR = '#';

/** The straddle a row's key belongs to, whichever split the row is. */
export const baseRowKey = (key) => String(key).split(SPLIT_KEY_SEPARATOR)[0];

/**
 * The row as a position bought with this budget.
 *
 * Rewrites the row's own position fields, so sorting, ranking and CSV follow the
 * real position without any sizing logic of their own. `Net Debit` becomes what
 * was actually spent, per share, which makes every return a return on that.
 *
 * A budget may carry a `key` and `label` naming its split. The key extends the
 * row's, because one straddle can appear once per split.
 *
 * Returns the same object when sizing is off, so 1-contract behavior can't change.
 */
export function sizeStraddleRow(row, sizing) {
  if (!isSizingActive(sizing)) return row;
  const { call, put } = row._legs || {};
  if (!call || !put) return row;

  const side = (leg, budget) => {
    const contracts = contractsForBudget(budget, leg.premium);
    const costCents = contractCents(leg.premium);
    return {
      contracts,
      contractCost: costCents / 100,
      spent: (contracts * costCents) / 100,
      budget: Number(budget),
      overBudget: costCents > Math.round(Number(budget) * 100),
    };
  };
  const callSide = side(call, sizing.callBudget);
  const putSide = side(put, sizing.putBudget);
  if (!callSide.contracts || !putSide.contracts) return row;

  const spent = (Math.round(callSide.spent * 100) + Math.round(putSide.spent * 100)) / 100;
  const budget = callSide.budget + putSide.budget;
  const debit = spent / 100;
  const overBudget = spent > budget;

  const netGreek = (key) => {
    if (call[key] === null || call[key] === undefined) return null;
    if (put[key] === null || put[key] === undefined) return null;
    return callSide.contracts * call[key] + putSide.contracts * put[key];
  };

  const crossing = (leg, contracts) => (
    leg.bid === null || leg.bid === undefined || leg.ask === null || leg.ask === undefined
      ? null
      : contracts * Math.max(leg.ask - leg.bid, 0)
  );
  const callCrossing = crossing(call, callSide.contracts);
  const putCrossing = crossing(put, putSide.contracts);

  return {
    ...row,
    _key: sizing.key ? `${row._key}${SPLIT_KEY_SEPARATOR}${sizing.key}` : row._key,
    _size: {
      call: callSide, put: putSide, spent, budget, overBudget, label: sizing.label ?? null,
    },
    Split: sizing.label,
    'Net Debit': debit,
    Cost: spent,
    'Max Loss': spent,
    'Max Downside Profit': Math.max(putSide.contracts * parseFloat(put.strike) - debit, 0) * 100,
    'Round-Trip Spread %': callCrossing === null || putCrossing === null
      ? null
      : ((callCrossing + putCrossing) / debit) * 100,
    'Net Delta': netGreek('delta'),
    'Net Theta': netGreek('theta'),
    'Net Vega': netGreek('vega'),
    'Budget Fit': overBudget ? 'Over budget' : 'Within budget',
  };
}

/**
 * One row per straddle per budget, or the same rows untouched with no usable budget.
 * `showSplit` tells the Cost column to name the split only when there's more than one.
 */
export function sizeStraddleRows(rows, budgets) {
  const usable = (budgets || []).filter(isSizingActive);
  if (usable.length === 0) return rows;
  return rows.flatMap((row) => {
    const sized = usable.map((budget) => sizeStraddleRow(row, budget));
    // A leg with no premium can't be sized by any budget. One copy, not duplicates.
    if (sized[0] === row) return [row];
    return sized.map((one) => ({ ...one, _size: { ...one._size, showSplit: usable.length > 1 } }));
  });
}

/** Contracts per leg: what the budget bought, or one of each with no budget. */
function legCounts(row) {
  return row._size
    ? { call: row._size.call.contracts, put: row._size.put.contracts }
    : { call: 1, put: 1 };
}

// ---------------------------------------------------------------------------
// Core pricing
// ---------------------------------------------------------------------------

/**
 * Value one leg at a horizon, given the stock price the column assumes by then.
 *
 * `settled` means this leg expired on or before the horizon. Its `stock` is then
 * the price on its own expiry date, which is the number worth showing in a tooltip.
 */
export function priceLeg(leg, spot, horizonPrice, horizon, { moveTiming = MOVE_STEADY, ivShift = 0 } = {}) {
  const days = legDays(leg);
  const strike = parseFloat(leg.strike);
  const premium = parseFloat(leg.premium);
  const settled = horizon >= days;

  const share = horizon > 0 ? Math.min(horizon, days) / horizon : 1;
  const stock = moveTiming === MOVE_EARLY
    ? horizonPrice
    : spot + (horizonPrice - spot) * share;

  const T = settled ? 0 : (days - horizon) / 365;
  const iv = parseFloat(leg.iv) * (settled ? 1 : 1 + ivShift);
  const value = blackScholes(stock, strike, T, RISK_FREE_RATE, iv, String(leg.option_type).toLowerCase());

  return {
    value,
    stock,
    settled,
    settledDay: settled ? days : null,
    premium,
    pnl: (value - premium) * 100,
    returnPct: premium > 0 ? ((value - premium) / premium) * 100 : null,
  };
}

/** Value of the whole position at a horizon stock price, in per-share terms. */
function straddleValueAt(row, horizonPrice, horizon, options) {
  const spot = parseFloat(row['Underlying Price']);
  const { call, put } = row._legs;
  const counts = legCounts(row);
  return counts.call * priceLeg(call, spot, horizonPrice, horizon, options).value
    + counts.put * priceLeg(put, spot, horizonPrice, horizon, options).value;
}

/** A priced leg as held: its dollars scale with the count, its % doesn't. */
const forPosition = (leg, contracts) => ({ ...leg, contracts, pnl: leg.pnl * contracts });

/**
 * One scenario cell: both legs and the net position.
 *
 * Net return is on the debit and each leg's on its own premium, so leg dollars sum
 * to net dollars but leg percentages don't sum to net percent. With a budget set,
 * the debit is what was actually spent and each leg counts every contract bought.
 *
 * Returns null when the row can't be priced or the move takes the stock to zero.
 */
export function computeStraddleCell(row, horizon, scenarioPct, options = {}) {
  const spot = parseFloat(row['Underlying Price']);
  const debit = parseFloat(row['Net Debit']);
  const legs = row._legs;
  if (!legs || !legs.call || !legs.put || !(spot > 0) || !(debit > 0)) return null;

  const horizonPrice = spot * (1 + scenarioPct / 100);
  if (horizonPrice <= 0) return null;

  const counts = legCounts(row);
  const call = forPosition(priceLeg(legs.call, spot, horizonPrice, horizon, options), counts.call);
  const put = forPosition(priceLeg(legs.put, spot, horizonPrice, horizon, options), counts.put);
  const value = counts.call * call.value + counts.put * put.value;
  const returnPct = ((value - debit) / debit) * 100;

  return {
    scenarioPct,
    horizonPrice,
    call,
    put,
    value,
    pnl: (value - debit) * 100,
    returnPct,
    fullySettled: call.settled && put.settled,
  };
}

/** Every scenario cell for one row, keyed by scenario %. */
export function computeStraddleHeatmapRow(row, horizon, scenarios, options = {}) {
  const result = {};
  for (const pct of scenarios) {
    result[pct] = computeStraddleCell(row, horizon, pct, options);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Breakevens
// ---------------------------------------------------------------------------

/**
 * Stock prices at the horizon where the position breaks even.
 *
 * The single-leg and vertical searches assume value only moves one way as the
 * stock moves. A straddle's doesn't, so there are up to two answers. Each leg's
 * value is convex in its stock price, and that price is linear in the horizon
 * price under both move timings, so total P&L is convex: find the minimum, then
 * bisect outward on each side.
 *
 * Returns { low, high, lowPct, highPct, alwaysProfitable }:
 *   - low is null when the stock reaching zero still can't recover the debit
 *   - alwaysProfitable (both null) when no price loses money
 *   - low === high when the minimum just touches zero
 */
export function findStraddleBreakevens(row, horizon, options = {}) {
  const none = { low: null, high: null, lowPct: null, highPct: null, alwaysProfitable: false };
  const spot = parseFloat(row['Underlying Price']);
  const debit = parseFloat(row['Net Debit']);
  const legs = row._legs;
  if (!legs || !legs.call || !legs.put || !(spot > 0) || !(debit > 0)) return none;

  const pnlAt = (price) => straddleValueAt(row, price, horizon, options) - debit;
  const toPct = (price) => (price === null ? null : ((price - spot) / spot) * 100);
  const reach = Math.max(parseFloat(legs.call.strike), parseFloat(legs.put.strike), spot);
  const floor = 0.01;

  // Golden-section search for the minimum. A flat bottom (both legs worthless
  // between their strikes) still converges somewhere on it.
  const ratio = (Math.sqrt(5) - 1) / 2;
  let a = floor;
  let b = reach * 4;
  let c = b - ratio * (b - a);
  let d = a + ratio * (b - a);
  let fc = pnlAt(c);
  let fd = pnlAt(d);
  for (let i = 0; i < 48; i += 1) {
    if (fc <= fd) {
      b = d; d = c; fd = fc;
      c = b - ratio * (b - a);
      fc = pnlAt(c);
    } else {
      a = c; c = d; fc = fd;
      d = a + ratio * (b - a);
      fd = pnlAt(d);
    }
  }
  const bottom = fc <= fd ? c : d;
  const lowest = Math.min(fc, fd);

  if (lowest > BREAKEVEN_EPSILON) return { ...none, alwaysProfitable: true };
  if (lowest >= -BREAKEVEN_EPSILON) {
    return { ...none, low: bottom, high: bottom, lowPct: toPct(bottom), highPct: toPct(bottom) };
  }

  const bisect = (losing, profitable) => {
    let lo = losing;
    let hi = profitable;
    for (let i = 0; i < 48; i += 1) {
      const mid = (lo + hi) / 2;
      if (pnlAt(mid) < 0) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2;
  };

  const low = pnlAt(floor) >= 0 ? bisect(bottom, floor) : null;

  let upper = reach * 4;
  while (pnlAt(upper) < 0 && upper < reach * 64) upper *= 2;
  const high = pnlAt(upper) >= 0 ? bisect(bottom, upper) : null;

  return { low, high, lowPct: toPct(low), highPct: toPct(high), alwaysProfitable: false };
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

/** How far today's model value misses the debit, as a %. Catches frozen quotes. */
export function modelGapPct(row) {
  const spot = parseFloat(row['Underlying Price']);
  const debit = parseFloat(row['Net Debit']);
  const legs = row._legs;
  if (!legs || !legs.call || !legs.put || !(spot > 0) || !(debit > 0)) return null;
  const valueToday = (leg) => blackScholes(
    spot, parseFloat(leg.strike), legDays(leg) / 365, RISK_FREE_RATE, parseFloat(leg.iv),
    String(leg.option_type).toLowerCase()
  );
  const counts = legCounts(row);
  const value = counts.call * valueToday(legs.call) + counts.put * valueToday(legs.put);
  return (Math.abs(value - debit) / debit) * 100;
}

export function hasModelGap(row) {
  const gap = modelGapPct(row);
  return gap !== null && gap > MODEL_GAP_LIMIT_PCT;
}

/** Why a row can't be trusted at the top of a ranking, or null if it can. */
export function unrankedReason(row, horizon) {
  if (isFullySettled(row, horizon)) return 'Both legs expire before this horizon';
  if (row['Quality'] === 'Poor') return 'Poor quote quality';
  if (hasModelGap(row)) return "Model doesn't reproduce today's quote";
  return null;
}

/** Net return on the whole straddle at each scenario that can be priced. */
function scenarioReturns(row, horizon, scenarios, options) {
  return (scenarios || [])
    .map((pct) => computeStraddleCell(row, horizon, pct, options)?.returnPct)
    .filter((v) => v !== null && v !== undefined);
}

/**
 * A row's score under a rank-by option, or null when it can't be scored.
 * Both ways, Highest gain and Average are net returns on the whole straddle (higher
 * wins), so they don't change with the leg being shown. Breakeven is a % move (lower
 * wins).
 *
 * Breakeven scores the HARDER direction, for the same reason Both ways takes the
 * worse return: the position has to work whichever way the stock goes. Scoring the
 * easier side would put a lopsided pair (call +2%, put -40%) near the top.
 */
export function scoreStraddle(row, horizon, { rankBy, plannedMove, scenarios, breakevens, ...options }) {
  if (rankBy === RANK_BREAKEVEN) {
    const be = breakevens || findStraddleBreakevens(row, horizon, options);
    if (be.lowPct === null || be.highPct === null) return null;
    return Math.max(Math.abs(be.lowPct), Math.abs(be.highPct));
  }
  if (rankBy === RANK_HIGHEST) {
    const returns = scenarioReturns(row, horizon, scenarios, options);
    return returns.length ? Math.max(...returns) : null;
  }
  if (rankBy === RANK_AVERAGE) {
    const returns = scenarioReturns(row, horizon, scenarios, options);
    return returns.length ? returns.reduce((sum, v) => sum + v, 0) / returns.length : null;
  }
  const up = computeStraddleCell(row, horizon, plannedMove, options);
  const down = computeStraddleCell(row, horizon, -plannedMove, options);
  return up && down ? Math.min(up.returnPct, down.returnPct) : null;
}

/**
 * Attach `_score`, `_rank` and `_unranked` to rows that carry a `_horizon`.
 *
 * Unranked rows stay in the list with a stated reason. They are excluded because
 * stale marks look cheap and would otherwise fill the top of the ranking.
 *
 * `enabled` is false in percent mode, where rows have different horizons and their
 * scores can't be compared.
 */
export function rankStraddles(rows, { enabled = true, ...scoring }) {
  const scored = rows.map((row) => {
    const reason = enabled ? unrankedReason(row, row._horizon) : 'Ranking needs a fixed horizon';
    const score = reason
      ? null
      : scoreStraddle(row, row._horizon, { ...scoring, breakevens: row._breakevens });
    return {
      ...row,
      _score: score,
      _rank: null,
      _unranked: reason || (score === null ? 'Could not be scored' : null),
    };
  });

  const lowerWins = scoring.rankBy === RANK_BREAKEVEN;
  scored
    .filter((row) => row._score !== null)
    .sort((a, b) => (lowerWins ? a._score - b._score : b._score - a._score))
    .forEach((row, index) => { row._rank = index + 1; });
  return scored;
}

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

/** Annualize a return, or null when it can't be. */
function annualize(returnPct, days) {
  if (!(days > 0) || returnPct === null || returnPct === undefined) return null;
  const value = (Math.pow(1 + returnPct / 100, 365 / days) - 1) * 100;
  return Number.isFinite(value) ? value : null;
}

/** Flat simulation fields for one row at one horizon and one move. */
export function simulateStraddle(row, horizon, scenarioPct, options = {}) {
  const cell = computeStraddleCell(row, horizon, scenarioPct, options);
  if (!cell) return row;
  return {
    ...row,
    'Days to Exit': horizon,
    'Underlying Gain %': scenarioPct,
    'Simulated Underlying': cell.horizonPrice,
    'Simulated Call': cell.call.value,
    'Simulated Put': cell.put.value,
    'Simulated Value': cell.value * 100,
    'Simulated P&L': cell.pnl,
    'Return on Debit %': cell.returnPct,
    'Ann. Return': annualize(cell.returnPct, horizon),
  };
}

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

// Shared because toLocaleString builds a new formatter on every call, which is far
// too slow for numbers repeated across thousands of table cells.
const WHOLE_NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const TWO_DECIMALS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The number a cell shows for a view (net or one leg) and a display mode.
 * Multiplier is return % over the size of the move, so a move in either direction
 * reads the same, and it has no meaning at 0%.
 */
export function cellMetric(cell, view, displayMode) {
  if (!cell) return null;
  const source = view === VIEW_CALL ? cell.call : view === VIEW_PUT ? cell.put : cell;
  if (displayMode === DISPLAY_PNL) return source.pnl;
  if (displayMode === DISPLAY_MULTIPLIER) {
    if (!cell.scenarioPct || source.returnPct === null) return null;
    return source.returnPct / Math.abs(cell.scenarioPct);
  }
  return source.returnPct;
}

/** The return a cell should be colored by, in every display mode. */
export function cellReturn(cell, view) {
  if (!cell) return null;
  if (view === VIEW_CALL) return cell.call.returnPct;
  if (view === VIEW_PUT) return cell.put.returnPct;
  return cell.returnPct;
}

/**
 * Colored by return in every display mode. The shared multiplier palette blanks
 * anything at or below zero, which would hide this position's entire loss zone.
 */
export function getStraddleCellColor(returnPct) {
  return getColorForGainPct(returnPct);
}

export function formatReturn(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const num = Number(value);
  if (Math.abs(num) < 1000) return `${num.toFixed(1)}%`;
  return `${WHOLE_NUMBER.format(Math.round(num))}%`;
}

export function formatDollars(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const num = Number(value);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs >= 1000) return `${sign}$${WHOLE_NUMBER.format(abs)}`;
  return `${sign}$${abs.toFixed(abs >= 100 ? 0 : 2)}`;
}

/** Keeps its sign: the shared formatMultiplier blanks losses, hiding them here. */
export function formatMultiplierValue(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return `${Number(value).toFixed(1)}x`;
}

export function formatCell(value, displayMode) {
  if (displayMode === DISPLAY_PNL) return formatDollars(value);
  if (displayMode === DISPLAY_MULTIPLIER) return formatMultiplierValue(value);
  return formatReturn(value);
}

/** Score text for the rank column. Breakeven scores are a required move. */
export function formatScore(score, rankBy) {
  if (score === null || score === undefined) return '--';
  return rankBy === RANK_BREAKEVEN ? `±${Number(score).toFixed(1)}%` : formatReturn(score);
}

/** Dollars, dropping empty cents: "$1,075" but "$145.34". */
export function formatMoney(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return '--';
  return `$${(Number.isInteger(num) ? WHOLE_NUMBER : TWO_DECIMALS).format(num)}`;
}

/** How a budget turned into contracts, one line per fact, for the Cost hover. */
export function describeSize(row) {
  const size = row._size;
  if (!size) return '';
  const sideLine = (name, side) => (
    `${name}: ${side.contracts} x ${formatMoney(side.contractCost)} = ${formatMoney(side.spent)} of ${formatMoney(side.budget)}`
  );
  const lines = [sideLine('Call', size.call), sideLine('Put', size.put)];
  if (size.label) lines.unshift(`${size.label} split`);
  const callShare = Math.round((size.call.spent / size.spent) * 100);
  lines.push(
    `Spent ${formatMoney(size.spent)} of ${formatMoney(size.budget)} (${callShare}% / ${100 - callShare}%)`
  );
  [['call', size.call], ['put', size.put]].forEach(([name, side]) => {
    if (side.overBudget) {
      lines.push(`One ${name} costs ${formatMoney(side.contractCost)}, over its ${formatMoney(side.budget)}`);
    }
  });
  return lines.join('\n');
}

export function formatPrice(value) {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '--';
  return `$${TWO_DECIMALS.format(num)}`;
}
